import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Shield,
  GraduationCap,
  Users,
  Scale,
  HardDrive,
  Server,
  CheckCircle2,
  ArrowRight,
  Lock,
  Eye,
  AlertTriangle,
  Calendar,
  Clock,
  BookOpen
} from 'lucide-react';
import { cyberSecurityServices, cyberSecurityBenefits, blogPosts } from '../data/mock';

const iconMap = {
  Shield,
  GraduationCap,
  Users,
  Scale,
  HardDrive,
  Server
};

const CyberSecurityServices = () => {
  // Filter cybersecurity blog posts
  const cyberSecurityBlogs = blogPosts.filter(post => 
    post.category === 'Cybersecurity'
  ).slice(0, 3); // Show top 3 recent posts
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-blue-500/20 text-blue-200 border-blue-400 mb-4">
                Cybersecurity Solutions
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                Protect Your Digital{' '}
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Assets & Infrastructure
                </span>
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Comprehensive cybersecurity solutions to safeguard your business from evolving threats. 
                From VAPT testing to compliance consulting, we secure your digital future.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
                    Book Security Assessment
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1614064548237-096f735f344f?w=800"
                alt="Cyber Security"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                <div className="flex items-center space-x-4">
                  <Shield className="w-12 h-12 text-green-400" />
                  <div>
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-blue-200">Security Support</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Cybersecurity Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive security solutions to protect your business from cyber threats
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {cyberSecurityServices.map((service, index) => {
              const Icon = iconMap[service.icon] || Shield;
              return (
                <Card
                  key={index}
                  className="p-8 hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-200 group"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advanced Security Offerings */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Advanced Protection</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Advanced Cybersecurity Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Next-generation security services for comprehensive threat protection
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Threat Intelligence</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Stay ahead of emerging threats with real-time threat intelligence feeds, dark web monitoring, and proactive threat hunting services.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  24/7 Threat Monitoring
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Dark Web Surveillance
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Incident Response Planning
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Zero Trust Implementation</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Implement Zero Trust architecture with continuous verification, micro-segmentation, and least-privilege access controls.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Identity & Access Management
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Network Segmentation
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Multi-Factor Authentication
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Security Operations Center (SOC)</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Managed SOC services with advanced SIEM, log analysis, and rapid incident response capabilities for comprehensive protection.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  SIEM Integration & Monitoring
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Security Event Analysis
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
                  Rapid Incident Response
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Security Services?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Affordable, ethical, and professional cybersecurity solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cyberSecurityBenefits.map((benefit, index) => (
              <Card
                key={index}
                className="p-6 text-center bg-white hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200"
              >
                <CheckCircle2 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <p className="font-semibold text-gray-900">{benefit}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-12 text-white">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Security by the Numbers</h2>
              <p className="text-xl text-blue-100">
                Protecting businesses with proven expertise
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <Lock className="w-12 h-12 mx-auto mb-3 text-blue-400" />
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-blue-200">Security Audits</div>
              </div>
              <div className="text-center">
                <Eye className="w-12 h-12 mx-auto mb-3 text-cyan-400" />
                <div className="text-4xl font-bold mb-2">99.8%</div>
                <div className="text-blue-200">Threat Detection</div>
              </div>
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
                <div className="text-4xl font-bold mb-2">10K+</div>
                <div className="text-blue-200">Vulnerabilities Fixed</div>
              </div>
              <div className="text-center">
                <Shield className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <div className="text-4xl font-bold mb-2">100%</div>
                <div className="text-blue-200">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cybersecurity Blog Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 mb-4">
              <BookOpen className="w-4 h-4 inline mr-2" />
              Security Insights
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Latest Cybersecurity Articles
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed with expert insights, best practices, and latest security trends
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {cyberSecurityBlogs.map((post) => (
              <Card 
                key={post.id}
                className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-blue-600 text-white">
                      {post.category}
                    </Badge>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="outline" className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all">
                      Read Article
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/blog">
              <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
                View All Security Articles
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Secure Your Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a free security assessment and protect your digital assets today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg">
                Book Free Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919315649601">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg">
                Call Security Expert
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CyberSecurityServices;
